import h from 'hyperscript';
import { t } from 'javascripts/utils/withTranslation';
import icon from 'components/_particles/icon/icon';
import Swipe from 'javascripts/utils/Swipe';

export default class Slider {
  constructor($slider, mq, config) {
    this.config = {
      showDots: false,
      scrollContainerClass: '.slider__slides-wrap',
      itemsContainerClass: '.slider__slides',
      itemClass: '.slider__slide',
      // Add this if there is nothing to focus in the slide (e.g.figure)
      focusableItemsClass: '',
      // This will enable the resizing of vertical images so that the width is not 100% but restricted to a specific height and centered
      resizeVerticalImages: true,
      itemsToScroll: 1,
      itemsVisible: 1,
      positionControlsOnImages: true,
      showCount: false,
      ...config,
    };
    this.$slider = $slider;
    this.$scrollContainer = $slider.querySelector(
      this.config.scrollContainerClass
    );
    this.$itemsContainer = $slider.querySelector(
      this.config.itemsContainerClass
    );
    this.$items = $slider.querySelectorAll(this.config.itemClass);
    this.$focusableItems = this.config.focusableItemsClass
      ? this.$slider?.querySelectorAll(this.config.focusableItemsClass)
      : null;
    this.allowResizeVerticalImages = this.config.resizeVerticalImages;
    this.itemsToScroll = this.config.itemsToScroll;
    this.itemsVisible = this.config.itemsVisible;
    this.showDots = this.config.showDots;
    this.showCount = this.config.showCount;
    this.positionControlsOnImages = this.config.positionControlsOnImages;
    this.numberOfSlides = this.$items?.length || 0;
    this.isDesktop = mq.matches;
    this.activeIndex = 0;
    this.minSlides = this.numberOfSlides > 1;
    this.maxIndex = this.getMaxIndex();
    this.slideWidth = this.getActiveSlideWidth();
    this.$slidesIndexes = [...this.$items].map((slide, index) => index);
    this.bindedResize = this.handleResize.bind(this);
    this.bindedFocusIn = this.focusIn.bind(this);
    this.bindedSelectTab = this.selectTab.bind(this);
    this.windowWidth = window.innerWidth;

    if (this.itemsToScroll < this.numberOfSlides) {
      this.initControls();
      this.initEvents();
    }
  }

  deconstructor() {
    this.removeArrows();
    this.removeDots();
    this.deinitSwiping();
    window.removeEventListener('resize', this.bindedResize);
    window.removeEventListener('selectTab', this.bindedSelectTab);
    this.$itemsContainer?.removeEventListener('focusin', this.bindedFocusIn);
  }

  initControls() {
    if (!this.isDesktop) {
      this.initSwiping();
    }

    if (this.itemsToScroll < this.$items.length) {
      this.renderDots();
      this.updateArrows();
    }

    if (this.showCount) {
      this.renderSlideCount();
    }

    this.sizeVerticalImages();
    this.positionControls();
  }

  initEvents() {
    if (this.$focusableItems) {
      // activate Focus for non focussable Elements like figures
      this.$focusableItems.forEach(($item) => {
        $item.setAttribute('tabindex', '0');
      });
    }

    window.addEventListener('resize', this.bindedResize);
    window.addEventListener('selectTab', this.bindedSelectTab);
    if (this.$itemsContainer) {
      this.$itemsContainer.addEventListener('focusin', this.bindedFocusIn);
      this.$itemsContainer.addEventListener('mousedown', () => {
        this.mousedown = true;
      });
      this.$itemsContainer.addEventListener('mouseup', () => {
        this.mousedown = false;
      });
      window.addEventListener('focusin', () => {
        this.mousedown = false;
      });
      window.addEventListener('blur', () => {
        this.mousedown = true;
      });
    }

    this.bindedNextSlide = this.nextSlide.bind(this);
    this.bindedPrevSlide = this.prevSlide.bind(this);
    this.bindedSwipeLeft = this.handleOnLeftSwipe.bind(this);
    this.bindedSwipeRight = this.handleOnRightSwipe.bind(this);
  }

  focusIn = (event) => {
    if (event.target && !this.mousedown) {
      const className = this.$items[0].classList[0];
      this.$scrollContainer.scrollLeft = 0;
      const $slide = event.target.closest(`.${className}`);
      const index = Array.prototype.indexOf.call(this.$items, $slide);
      this.slideTo(index);
    }
  };

  selectTab = () => {
    this.slideWidth = this.getActiveSlideWidth();
    this.positionControls();
  };

  initSwiping() {
    this.swipe = new Swipe(this.$itemsContainer);
    this.swipe.onLeft(this.bindedSwipeLeft);
    this.swipe.onRight(this.bindedSwipeRight);
  }

  deinitSwiping() {
    if (this.swipe && this.swipe.deconstructor) {
      this.swipe.deconstructor();
    }
  }

  handleOnLeftSwipe = () => {
    // prevents over-scrolling the last slide on swipe when itemsToScroll = 1
    if (
      this.activeIndex <= this.maxIndex - this.itemsToScroll ||
      this.itemsToScroll > 1
    ) {
      this.nextSlide();
      this.updateDots();
      this.updateArrows();
      this.sizeVerticalImages();
    }
  };

  handleOnRightSwipe = () => {
    this.prevSlide();
    this.updateDots();
    this.updateArrows();
    this.sizeVerticalImages();
  };

  getMaxIndex() {
    return this.$items.length - this.itemsVisible;
  }

  getActiveSlideWidth() {
    return this.$items[this.activeIndex]?.offsetWidth;
  }

  handleResize() {
    if (window.innerWidth === this.windowWidth) {
      return;
    }
    this.windowWidth = window.innerWidth;
    this.activeIndex = 0;
    this.slideWidth = this.getActiveSlideWidth();
    this.maxIndex = this.getMaxIndex();
    this.updateSlidePosition();
    this.removeArrows();
    this.positionControls();

    if (!this.isDesktop) {
      this.sizeVerticalImages();
    }

    if (this.itemsToScroll < this.$items.length) {
      this.renderDots();
      this.updateArrows();
    }
  }

  removeArrows() {
    if (this.$controlNext && this.$controlNext?.parentNode) {
      this.$controlNext?.parentNode.removeChild(this.$controlNext);
    }

    if (this.$controlPrev && this.$controlPrev?.parentNode) {
      this.$controlPrev?.parentNode.removeChild(this.$controlPrev);
    }

    this.$slider.querySelectorAll('.slider__arrow-wrap')?.forEach((node) => {
      node?.parentNode?.removeChild(node);
    });
  }

  removeDots() {
    this.$slider.querySelectorAll('.slider__dot-arrows')?.forEach((node) => {
      node?.parentNode?.removeChild(node);
    });
  }

  nextSlide() {
    if (this.activeIndex < this.maxIndex) {
      let scrollToIndex = this.maxIndex;
      if (this.activeIndex < this.maxIndex - this.itemsToScroll) {
        scrollToIndex = this.activeIndex + this.itemsToScroll;
      }
      this.activeIndex = scrollToIndex;
      this.updateSlidePosition();
    }
  }

  prevSlide() {
    if (this.activeIndex > 0) {
      let scrollToIndex = this.activeIndex - 1;
      if (this.activeIndex >= this.itemsToScroll) {
        scrollToIndex = this.activeIndex - this.itemsToScroll;
      }
      this.activeIndex = scrollToIndex;
      this.updateSlidePosition();
    }
  }

  updateSlidePosition() {
    this.$itemsContainer.style.transform = `translateX(${
      -1 * this.activeIndex * this.slideWidth
    }px)`;
    this.positionControls();
    this.updateDots();
  }

  slideTo(index) {
    this.activeIndex = index;
    if (this.activeIndex <= this.maxIndex) {
      this.updateSlidePosition();
      this.updateArrows();
      this.sizeVerticalImages();
      this.updateDots();
    }
  }

  handleDotClick = (index) => {
    this.activeIndex = index;
    this.updateDots();
    this.updateSlidePosition();
    this.updateArrows();
    this.updateSlideCount();
    this.sizeVerticalImages();
  };

  renderDots() {
    this.removeDots();

    if (!this.showDots) {
      return;
    }

    const $dots = h('div.dots');

    if (this.$items.length > 1 && this.$items.length !== this.itemsToScroll) {
      this.$items.forEach((_, index) => {
        if (
          index % this.itemsToScroll === 0 ||
          index === 0 ||
          (index === this.maxIndex && index % this.itemsToScroll === 0)
        ) {
          const dotContent = h(
            'span.u-hidden-visually',
            `${t('Zu Kachel')}: ${index}`
          );
          const dot = h('span.dots__dot', dotContent);
          const dotWrapper = h(
            `button.dots__dot-wrapper${
              index === this.activeIndex || index === this.maxIndex
                ? '.dots__dot--active'
                : ''
            }`,
            {
              type: 'button',
              tabIndex: '-1',
              ariaCurrent: index === this.activeIndex ? 'location' : null,
              onclick: () => this.handleDotClick(index),
            },
            dot
          );
          $dots.appendChild(dotWrapper);
        }
      });
      this.$dotsWrapper = h('.slider__dots', $dots);
      this.$arrowDotWrapper = h('.slider__dot-arrows', this.$dotsWrapper);
      this.$scrollContainer.appendChild(this.$arrowDotWrapper);

      this.updateDots();
    }
  }

  updateDots() {
    if (!this.showDots) {
      return;
    }

    this.$dotsWrapper
      ?.querySelectorAll('.dots__dot-wrapper')
      .forEach(($dot, index, $dots) => {
        if (
          index * this.itemsToScroll === this.activeIndex ||
          (index === this.getMaxIndex() &&
            this.activeIndex === this.getMaxIndex())
        ) {
          $dots.forEach(($do) => {
            $do.classList.remove('dots__dot--active');
            $do.removeAttribute('aria-current');
          });
          $dot.classList.add('dots__dot--active');
          $dot.setAttribute('aria-current', 'location');
        }
      });
  }

  handleLeftArrowClick() {
    if (!this.isDesktop) {
      this.handleOnRightSwipe();
    } else {
      this.prevSlide();
    }
    this.updateArrows();
    this.updateSlideCount();
    this.sizeVerticalImages();
  }

  handleRightArrowClick() {
    if (!this.isDesktop) {
      this.handleOnLeftSwipe();
    } else {
      this.nextSlide();
    }
    this.updateArrows();
    this.updateSlideCount();
    this.sizeVerticalImages();
  }

  updateArrows() {
    this.removeArrows();
    const bindedRightClick = this.handleRightArrowClick.bind(this);
    const bindedLeftClick = this.handleLeftArrowClick.bind(this);

    this.$controlPrev = h(
      '.slider__arrow-wrap.slider__arrow--prev',
      h(
        'button.slider__arrow',
        {
          onclick: bindedLeftClick,
          type: 'button',
          tabIndex: '-1',
          attrs: {
            'aria-label': t('Vorherige Inhalte einblenden'),
          },
        },
        icon({
          icon: 'slider-arrow-left',
          viewBox: '0 0 200 200',
          classes: ['slider__arrow-icon'],
        })
      )
    );

    this.$controlNext = h(
      '.slider__arrow-wrap.slider__arrow--next',
      h(
        'button.slider__arrow',
        {
          onclick: bindedRightClick,
          type: 'button',
          tabIndex: '-1',
          attrs: {
            'aria-label': t('Nächste Inhalte einblenden'),
          },
        },
        icon({
          icon: 'slider-arrow-right',
          viewBox: '0 0 200 200',
          classes: ['slider__arrow-icon'],
        })
      )
    );

    const $container =
      !this.showDots || this.isDesktop
        ? this.$scrollContainer
        : this.$arrowDotWrapper;

    if (this.activeIndex > 0) {
      $container.appendChild(this.$controlPrev);
    }

    if (this.activeIndex < this.getMaxIndex()) {
      $container.appendChild(this.$controlNext);
    }

    this.positionControls();
  }

  // Vertical images should not be 100% wide, but restricted to a specific height and centered
  sizeVerticalImages() {
    if (this.allowResizeVerticalImages) {
      this.$items?.forEach(($slide) => {
        const $figure = $slide.querySelector('.figure');

        if ($figure) {
          const $imageWrap = $figure.querySelector('.picture');
          const $img = $figure.querySelector('.picture__image');
          const $copyright = $figure.querySelector('.figure__copyright');

          if ($img) {
            const definedWidth = parseInt($img.getAttribute('width'), 10);
            const definedHeight = parseInt($img.getAttribute('height'), 10);
            const ratio = definedHeight / definedWidth;

            const isHorizontal = definedWidth <= definedHeight || ratio > 1;

            if ($img && isHorizontal) {
              $imageWrap.removeAttribute('style');
              $imageWrap.classList.add('slider__image-vertical');
              if (
                !$imageWrap.querySelector('.slider__copyright-wrap') &&
                $copyright
              ) {
                const $copyrightWrap = document.createElement('div');
                $copyrightWrap.classList.add('slider__copyright-wrap');
                $img.parentNode.insertBefore($copyrightWrap, $img);
                $copyrightWrap.appendChild($img);
                $copyrightWrap.appendChild($copyright);
              }
            }
          }
        }
      });
    }
  }

  // calculate the control positions depending on images
  positionControls() {
    if (
      !this.positionControlsOnImages ||
      (this.showDots && !this.isDesktop && !this.showCount)
    ) {
      return;
    }
    const $activeSlide = this.$items[this.activeIndex] || this.$items[0];

    if ($activeSlide) {
      const $activeSlideMedia = $activeSlide.querySelector('.figure__media');
      if ($activeSlideMedia) {
        const top = `${
          $activeSlideMedia.offsetTop + $activeSlideMedia.clientHeight / 2
        }px`;

        if (this.isDesktop) {
          this.$controlNext?.setAttribute('style', `top: ${top}`);
          this.$controlPrev?.setAttribute('style', `top: ${top}`);
        }

        if (this.$slideCountContainer && this.showCount) {
          this.$slideCountContainer.style.top = `${
            $activeSlideMedia.offsetTop + $activeSlideMedia.clientHeight + 8
          }px`;
        }
      }
    }
  }

  removeSlideCount() {
    const getNode =
      this.$slideCountContainer ||
      this.$slider.querySelector('.slider__slide-count');

    getNode?.parentNode?.removeChild(getNode);
  }

  // add initial count to slide controls
  renderSlideCount() {
    if (!this.showCount) {
      return;
    }
    this.removeSlideCount();
    const $slideCount = h(
      'span.slider__slide-count-inner',
      `1/${this.numberOfSlides}`
    );
    this.$slideCountContainer = h('.slider__slide-count');
    this.$slideCountContainer?.appendChild($slideCount);
    this.$scrollContainer?.appendChild(this.$slideCountContainer);
  }

  updateSlideCount() {
    if (!this.showCount) {
      return;
    }
    const $counter = this.$slider.querySelector('.slider__slide-count-inner');
    if ($counter) {
      $counter.innerText = `${this.activeIndex + 1}/${this.numberOfSlides}`;
    }
  }
}
